import { Form, Input, Button, Select, Space, Card } from "antd";
import moment from "moment";
import { useMemo } from "react";
import { useUpdateEducation } from "../../../../hooks/api/Tutors/UpdateProfile/useUpdateEducation";

type Props = {
  initialValues: {
    id: string;
    college: string;
    majors?: string;
    degree: string;
    enrolled: string;
    graduated: string | null;
  };
  onCancel: () => void;
};

const UpdateEducationForm = ({ initialValues, onCancel }: any) => {
  const [form] = Form.useForm();
  const { mutate, isLoading } = useUpdateEducation();

  const formInitialValues = useMemo(() => {
    return {
      ...initialValues,
      enrolled: initialValues.enrolled
        ? moment(initialValues.enrolled).format("YYYY-MM-DD")
        : "",
      graduated: initialValues.graduated
        ? moment(initialValues.graduated).format("YYYY-MM-DD")
        : "",
    };
  }, [initialValues]);

  const onFinish = (values: any) => {
    const formattedValues: any = {
      id: initialValues.id || initialValues._id,
    };

    // Only include fields that have changed
    if (values.college !== initialValues.college) {
      formattedValues.college = values.college;
    }
    if (values.majors !== initialValues.majors) {
      formattedValues.majors = values.majors;
    }
    if (values.degree !== initialValues.degree) {
      formattedValues.degree = values.degree;
    }

    const currentEnrolledDate = values.enrolled;
    const initialEnrolledDate = initialValues.enrolled
      ? moment(initialValues.enrolled).format("YYYY-MM-DD")
      : null;

    if (currentEnrolledDate !== initialEnrolledDate) {
      formattedValues.enrolled = values.enrolled
        ? moment.utc(values.enrolled, "YYYY-MM-DD").toISOString()
        : null;
    }

    const currentGraduatedDate = values.graduated;
    const initialGraduatedDate = initialValues.graduated
      ? moment(initialValues.graduated).format("YYYY-MM-DD")
      : null;

    if (currentGraduatedDate !== initialGraduatedDate) {
      formattedValues.graduated = values.graduated
        ? moment.utc(values.graduated, "YYYY-MM-DD").toISOString()
        : null;
    }

    // If nothing changed, just close (or maybe show a message?)
    // But for now, let's proceed if there are keys other than id
    if (Object.keys(formattedValues).length === 1) {
      onCancel();
      return;
    }

    mutate(formattedValues, {
      onSuccess: () => {
        onCancel();
      },
    });
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      initialValues={formInitialValues}
    >
      <Space
        direction="vertical"
        style={{ display: "flex", width: "100%", padding: 0 }}
      >
        <Card size="small" bordered={false}>
          <Form.Item name="college" label="College">
            <Input placeholder="Enter College Name" size="large" />
          </Form.Item>

          <Form.Item name="majors" label="Majors">
            <Input placeholder="Enter Majors" size="large" />
          </Form.Item>

          <Form.Item name="degree" label="Degree">
            <Select placeholder="Select Degree" size="large">
              <Select.Option value="Bachelors">Bachelor's</Select.Option>
              <Select.Option value="Masters">Masters</Select.Option>
              <Select.Option value="Doctorate">Doctorate</Select.Option>
              <Select.Option value="Associate">Associate</Select.Option>
              <Select.Option value="Others">
                Others (This includes HND & OND)
              </Select.Option>
            </Select>
          </Form.Item>

          <div className="flex flex-row gap-2">
            <div className="w-6/12">
              <Form.Item name="enrolled" label="Enrolled Date">
                <input
                  type="date"
                  className="w-full border-[1px] rounded-md border-gray300 p-2 outline-none h-[40px]"
                />
              </Form.Item>
            </div>
            <div className="w-6/12">
              <Form.Item name="graduated" label="Graduation Date">
                <input
                  type="date"
                  className="w-full border-[1px] rounded-md border-gray300 p-2 outline-none h-[40px]"
                />
              </Form.Item>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Update
            </Button>
          </div>
        </Card>
      </Space>
    </Form>
  );
};

export default UpdateEducationForm;
